/**
 * Demo-session helpers (Phase 2d demo hardening).
 *
 * Demo visitors sign in via the "Try full demo" button. We mark the
 * session in localStorage and surface a watermark banner plus a hard
 * cap on expensive operations:
 *
 *   - AI chat: 3 queries per demo session. Generous enough to feel
 *     out the routing (Haiku lookup, Sonnet analysis, file gen) but
 *     not so generous that a bored visitor can burn through the
 *     entity-level $25/mo AI budget.
 *
 * Server-side, the demo visitor is `board@helixpartners.io` with
 * role=viewer. That blocks every write/export/file-generation path
 * via the existing RoleGuard. This client-side cap is the
 * belt-and-suspenders: even read-only AI chats cost Anthropic dollars.
 */

const DEMO_KEY = 'nexus_demo_session';
const AI_COUNTER_KEY = 'nexus_demo_ai_queries';
export const DEMO_AI_CAP = 3;

export function isDemoSession(): boolean {
  if (typeof window === 'undefined') return false;
  try { return localStorage.getItem(DEMO_KEY) === '1'; } catch { return false; }
}

export function markDemoSession(flag: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (flag) localStorage.setItem(DEMO_KEY, '1');
    else {
      localStorage.removeItem(DEMO_KEY);
      localStorage.removeItem(AI_COUNTER_KEY);
    }
  } catch { /* storage full, quota, etc. - best-effort */ }
}

export function demoAIQueriesUsed(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(AI_COUNTER_KEY);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch { return 0; }
}

export function demoAIQueriesRemaining(): number {
  return Math.max(0, DEMO_AI_CAP - demoAIQueriesUsed());
}

/**
 * Increment the demo AI counter. Callers should check
 * `demoAIQueriesRemaining() > 0` before sending the request; this
 * is just the post-send bookkeeping.
 */
export function incrementDemoAIQuery(): void {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(AI_COUNTER_KEY, String(demoAIQueriesUsed() + 1)); } catch { /* */ }
}

/**
 * Returns a user-facing message to show when the cap is hit.
 */
export function demoCapMessage(): string {
  return `Demo mode: you've used your ${DEMO_AI_CAP} AI queries. Sign up for a full account to keep chatting — every plan includes a generous monthly Claude budget.`;
}
